import { access, stat, readdir } from 'fs/promises';
import { join, dirname, relative } from 'path';
import chalk from 'chalk';
import ora from 'ora';
import { confirm, success, error, info, formatDate, divider } from '../utils/prompt.js';
import { loadConfig } from '../utils/config-loader.js';
import { GitignoreManager } from '../utils/gitignore-manager.js';
import { convertToMemory, detectFormat } from '../converters/index.js';
import { ImportOptions, SourceFormat } from '../types/index.js';
import { IgnoreMatcher } from '../utils/ignore-matcher.js';

/**
 * Import command - convert CLAUDE.md or AGENTS.md to MEMORY.md
 */
export async function importCommand(options: ImportOptions): Promise<void> {
  const cwd = process.cwd();
  const config = await loadConfig(cwd);

  console.log(chalk.bold('\n📥 Import to MEMORY.md\n'));

  // Initialize ignore matcher
  const ignoreMatcher = new IgnoreMatcher(cwd, config);
  await ignoreMatcher.initialize();

  // Determine if we should do recursive import
  const from = options.from.toLowerCase();
  const isRecursive = from === 'claude' || from === 'claude.md' || from === 'agents' || from === 'agents.md' || from === 'codex' || from === 'gemini';

  if (isRecursive) {
    // Recursive mode: find all CLAUDE.md or AGENTS.md files
    const targetFormat = (from === 'claude' || from === 'claude.md') ? 'CLAUDE.md' : 'AGENTS.md';
    await importRecursive(cwd, targetFormat as SourceFormat, ignoreMatcher, config);
  } else {
    // Single file mode
    await importSingle(options.from, cwd, config);
  }

  console.log();
}

/**
 * Import a single file
 */
async function importSingle(from: string, cwd: string, config: any): Promise<void> {
  // Resolve source file
  const sourceFile = resolveSourceFile(from, cwd);

  // Check if source exists
  if (!(await fileExists(sourceFile))) {
    error(`Source file not found: ${sourceFile}`);
    process.exit(1);
  }

  // Detect source format
  const sourceFormat = detectFormat(sourceFile);
  if (!sourceFormat || sourceFormat === 'MEMORY.md') {
    error('Source must be CLAUDE.md or AGENTS.md');
    process.exit(1);
  }

  const targetFile = join(cwd, 'MEMORY.md');

  // Check if MEMORY.md already exists
  if (await fileExists(targetFile)) {
    const sourceStat = await stat(sourceFile);
    const targetStat = await stat(targetFile);

    console.log(chalk.yellow('⚠️  MEMORY.md already exists:\n'));
    console.log(`  Source (${chalk.cyan(sourceFormat)}):     ${formatDate(sourceStat.mtime)}`);
    console.log(`  Existing ${chalk.cyan('MEMORY.md')}:    ${formatDate(targetStat.mtime)}`);
    console.log();

    const shouldOverwrite = await confirm('Overwrite MEMORY.md?', false);
    if (!shouldOverwrite) {
      info('Import cancelled');
      return;
    }
  }

  // Perform conversion
  const spinner = ora('Converting...').start();

  try {
    await convertToMemory(sourceFile, targetFile, sourceFormat);
    spinner.succeed(`Imported ${chalk.cyan(sourceFormat)} → ${chalk.cyan('MEMORY.md')}`);
  } catch (err) {
    spinner.fail('Import failed');
    error((err as Error).message);
    process.exit(1);
  }

  // Handle .gitignore
  await handleGitignore(config, cwd);

  success('Import complete!');
  console.log();
  info('Next steps:');
  console.log(chalk.dim('  - Edit MEMORY.md as needed'));
  console.log(chalk.dim('  - Run "mem2 compile" to generate target files'));
  console.log(chalk.dim('  - Run "mem2 watch" to auto-sync changes'));
}

/**
 * Import all files recursively
 */
async function importRecursive(
  cwd: string,
  targetFormat: SourceFormat,
  ignoreMatcher: IgnoreMatcher,
  config: any
): Promise<void> {
  // Find all target files recursively
  const sourceFiles = await findSourceFiles(cwd, targetFormat, ignoreMatcher);

  if (sourceFiles.length === 0) {
    error(`No ${targetFormat} files found`);
    process.exit(1);
  }

  console.log(chalk.dim(`Found ${sourceFiles.length} ${targetFormat} file(s)\n`));

  let importedCount = 0;
  let skippedCount = 0;

  for (const sourceFile of sourceFiles) {
    const dirPath = dirname(sourceFile);
    const targetFile = join(dirPath, 'MEMORY.md');
    const relativePath = relative(cwd, dirPath);
    const displayPath = relativePath ? relativePath + '/' : '';

    // Check if MEMORY.md already exists
    if (await fileExists(targetFile)) {
      const sourceStat = await stat(sourceFile);
      const targetStat = await stat(targetFile);

      // Skip if MEMORY.md is newer
      if (targetStat.mtime >= sourceStat.mtime) {
        info(`Skipped ${displayPath}${targetFormat} (MEMORY.md is up-to-date)`);
        skippedCount++;
        continue;
      }
    }

    // Perform conversion
    const spinner = ora(`Importing ${displayPath}${targetFormat}...`).start();

    try {
      await convertToMemory(sourceFile, targetFile, targetFormat);
      spinner.succeed(`Imported ${displayPath}${targetFormat} → MEMORY.md`);
      importedCount++;
    } catch (err) {
      spinner.fail(`Failed to import ${displayPath}${targetFormat}`);
      error(`  ${(err as Error).message}`);
    }
  }

  console.log();

  if (importedCount === 0 && skippedCount > 0) {
    info('All MEMORY.md files are up-to-date');
  } else if (importedCount > 0) {
    // Handle .gitignore only once
    await handleGitignore(config, cwd);
    success(`Successfully imported ${importedCount} file(s)!`);
    if (skippedCount > 0) {
      info(`Skipped ${skippedCount} up-to-date file(s)`);
    }
    console.log();
    info('Next steps:');
    console.log(chalk.dim('  - Run "mem2 compile" to generate target files'));
    console.log(chalk.dim('  - Run "mem2 watch" to auto-sync changes'));
  }
}

/**
 * Recursively find all source files
 */
async function findSourceFiles(
  dir: string,
  targetFormat: SourceFormat,
  ignoreMatcher: IgnoreMatcher,
  basePath?: string
): Promise<string[]> {
  const results: string[] = [];
  const base = basePath || dir;

  try {
    const entries = await readdir(dir, { withFileTypes: true });

    for (const entry of entries) {
      const fullPath = join(dir, entry.name);
      const relativePath = relative(base, fullPath);

      // Check if should ignore (using relative path)
      if (ignoreMatcher.shouldIgnore(relativePath)) {
        continue;
      }

      if (entry.isDirectory()) {
        // Recursively search subdirectories
        const subResults = await findSourceFiles(fullPath, targetFormat, ignoreMatcher, base);
        results.push(...subResults);
      } else if (entry.isFile() && entry.name === targetFormat) {
        results.push(fullPath);
      }
    }
  } catch (err) {
    // Silently skip directories we can't read
  }

  return results;
}

/**
 * Resolve source file path
 */
function resolveSourceFile(from: string, cwd: string): string {
  // If it's a full path, use it
  if (from.includes('/') || from.includes('\\')) {
    return from;
  }

  // If it's just a format name, resolve to file
  const normalized = from.toLowerCase();
  if (normalized === 'claude' || normalized === 'claude.md') {
    return join(cwd, 'CLAUDE.md');
  }
  if (normalized === 'agents' || normalized === 'agents.md' || normalized === 'codex' || normalized === 'gemini') {
    return join(cwd, 'AGENTS.md');
  }

  // Otherwise treat as filename
  return join(cwd, from);
}

/**
 * Handle .gitignore configuration
 */
async function handleGitignore(config: any, cwd: string): Promise<void> {
  const gitignoreManager = new GitignoreManager(cwd);
  const recommendedEntries = GitignoreManager.getRecommendedEntries();

  if (!(await gitignoreManager.exists())) {
    // No .gitignore exists
    if (config.import.autoGitignore === 'ask') {
      console.log();
      divider();
      info('No .gitignore found');
      console.log();
      console.log(chalk.dim('  It\'s recommended to ignore compiled files and keep MEMORY.md'));
      console.log(chalk.dim('  as your single source of truth.\n'));
      console.log(chalk.dim('  Files to ignore:'));
      recommendedEntries.forEach((entry) => console.log(chalk.dim(`    - ${entry}`)));
      console.log();

      const shouldCreate = await confirm(
        'Create .gitignore with recommended entries?',
        true
      );

      if (shouldCreate) {
        await gitignoreManager.create(recommendedEntries);
        success('Created .gitignore');
        info('Configure this behavior in .mem2rc.json (import.autoGitignore)');
      }
    } else if (config.import.autoGitignore === 'auto') {
      await gitignoreManager.create(recommendedEntries);
      success('Created .gitignore with recommended entries');
    }
  } else {
    // .gitignore exists
    if (config.gitignore.recommendIgnoreCompiled === 'ask') {
      const missingEntries = [];
      for (const entry of recommendedEntries) {
        if (!(await gitignoreManager.isIgnored(entry))) {
          missingEntries.push(entry);
        }
      }

      if (missingEntries.length > 0) {
        console.log();
        divider();
        info('Recommendation: Add compiled files to .gitignore');
        console.log();
        console.log(chalk.dim('  Missing entries:'));
        missingEntries.forEach((entry) => console.log(chalk.dim(`    - ${entry}`)));
        console.log();

        const shouldAdd = await confirm(
          'Add these entries to .gitignore?',
          true
        );

        if (shouldAdd) {
          await gitignoreManager.addEntries(missingEntries);
          success('Updated .gitignore');
          info('Configure this behavior in .mem2rc.json (gitignore.recommendIgnoreCompiled)');
        }
      }
    } else if (config.gitignore.recommendIgnoreCompiled === 'auto') {
      await gitignoreManager.addEntries(recommendedEntries);
    }
  }
}

/**
 * Check if file exists
 */
async function fileExists(path: string): Promise<boolean> {
  try {
    await access(path);
    return true;
  } catch {
    return false;
  }
}
